/**
 * Search + SHEET-SPECIFIC filters (feature #5). The dropdowns are built from
 * the active sheet's own columns (see buildFacets), so they change with the tab.
 * Logs 'searched' and 'filter_used' events.
 */
import { useEffect, useRef, useState } from 'react';
import type { FilterFacet, ProductFilters } from '../../features/product/search';
import { hasActiveFilters } from '../../features/product/search';
import { logActivity } from '../../lib/activity/activityLogger';
import { setPresenceView } from '../../lib/presence';

interface Props {
  filters: ProductFilters;
  facets: FilterFacet[];
  category: string;
  onChange: (next: ProductFilters) => void;
  onClear: () => void;
}

export function SearchFilterBar({ filters, facets, category, onChange, onClear }: Props): JSX.Element {
  const [showFilters, setShowFilters] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced 'searched' logging + Live-monitor view.
  useEffect(() => {
    const query = filters.query.trim();
    if (!query) return;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      logActivity('searched', { query, category });
      setPresenceView({ view: 'search', label: `Searching "${query}"` });
    }, 800);
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
  }, [filters.query, category]);

  const activeCount = Object.keys(filters.attrs).length;

  const setColumn = (column: string, value: string | null) => {
    const attrs = { ...filters.attrs };
    if (value === null) delete attrs[column];
    else attrs[column] = value;
    onChange({ ...filters, attrs });
    if (value !== null) logActivity('filter_used', { filter: column, category });
  };

  return (
    <div className="bg-ground px-4 pb-1 pt-2.5">
      <div className="flex items-center gap-2">
        <input
          className="w-full rounded-2xl border-0 bg-chipbg px-4 py-2.5 text-[13px] text-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/30"
          type="search"
          placeholder="Search model, part no, CPU…"
          value={filters.query}
          onChange={(e) => onChange({ ...filters, query: e.target.value })}
        />
        <button
          type="button"
          className={`shrink-0 rounded-2xl px-3.5 py-2.5 text-[12.5px] font-semibold transition ${
            hasActiveFilters(filters) ? 'bg-brand-light text-brand' : 'bg-chipbg text-slate-600'
          }`}
          onClick={() => setShowFilters((s) => !s)}
          disabled={facets.length === 0}
        >
          Filters{activeCount > 0 ? ` (${activeCount})` : ''}
        </button>
      </div>

      {showFilters && facets.length > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {facets.map((f) => (
            <div key={f.name}>
              <label className="label truncate" title={f.name}>
                {f.label}
              </label>
              <select
                className="w-full rounded-xl border-0 bg-white px-3 py-2 text-[12.5px] text-ink shadow-mini focus:outline-none focus:ring-2 focus:ring-brand/30"
                value={filters.attrs[f.name] ?? ''}
                onChange={(e) => setColumn(f.name, e.target.value === '' ? null : e.target.value)}
              >
                <option value="">Any</option>
                {f.values.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <button
            type="button"
            className="col-span-2 pt-1 text-xs font-semibold text-slate-500 underline"
            onClick={onClear}
          >
            Clear all filters
          </button>
        </div>
      ) : null}
    </div>
  );
}
